import { endOfDay, getTime, startOfDay, toDate } from 'date-fns';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RangeKeyDict } from 'react-date-range';
import {
  MdCheck,
  MdKeyboardArrowDown,
  MdOutlineFilterAlt,
  MdOutlineSettingsBackupRestore,
} from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ReturnFilterStore } from 'zustand-store';
import { useAuth } from 'hooks';
import { QueryParam, ReturnFilterForm, ReturnFilterParam } from 'types';
import { Button, DateRangePicker, Select, Text } from 'components';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'helpers';
import { formatTimestamp, getFinalDate, getInitialDate } from 'helpers/date';
import {
  DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
  FontSize,
  FontWeight,
  LocalStorageKeys,
  RETURN_STATUSES,
  TEST_IDS,
  getDefaultReturnFilter,
  getDefaultReturnSearch,
  returnStatuses,
  routes,
} from 'commons';
import * as S from '../styles';

export function ReturnMoreFilter() {
  const firstRender = useRef(true);
  const { selectedStore } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    filteredBrand,
    filteredStore,
    returnFilter,
    setReturnFilter,
    setReturnSearch,
    reset,
  } = ReturnFilterStore.useStore();
  const [isOpen, setIsOpen] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);

  function clearUrlSearchParamFilter() {
    searchParams.delete(QueryParam.filter);
    setSearchParams(searchParams);
    removeLocalStorageItem(LocalStorageKeys.hadUrlReturnFilterApplied);
  }

  const {
    values,
    handleSubmit,
    handleReset,
    handleChange,
    setFieldValue,
    setValues,
    submitForm,
    resetForm,
  } = useFormik<ReturnFilterForm>({
    initialValues: returnFilter,

    onSubmit: (values) => {
      clearUrlSearchParamFilter();

      setIsOpen(false);

      const currValues = { ...values };
      const currReturnStatusesToString = String(currValues.returnStatuses);
      currValues.returnStatuses = currReturnStatusesToString
        .split(',')
        .filter((deliveryMode) => deliveryMode !== '');

      setReturnFilter(currValues);
    },

    onReset: (_, { setValues }) => {
      clearUrlSearchParamFilter();

      const defaultReturnFilter = getDefaultReturnFilter();
      const defaultReturnSearch = getDefaultReturnSearch();

      setReturnFilter(defaultReturnFilter);
      setReturnSearch(defaultReturnSearch);

      setTimeout(() => {
        setValues(defaultReturnFilter);
      }, 100);

      setIsOpen(false);
    },
  });

  function changeDateRange(dateRange: RangeKeyDict) {
    const { startDate, endDate } = dateRange.range1;

    if (!startDate || !endDate) return;

    const start = startOfDay(startDate);
    const end = endOfDay(endDate);

    setFieldValue('startDate', getTime(start));
    setFieldValue('endDate', getTime(end));
  }

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      searchParams.delete(QueryParam.filter);
      setSearchParams(searchParams);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  useEffect(() => {
    function filterByUrl() {
      const { pathname, search } = location;

      const params = new URLSearchParams(search);
      const filter = params.get(QueryParam.filter);

      if (filter) {
        reset({ store: filteredStore, brand: filteredBrand });
      }

      const startDate = getInitialDate({
        timeDifference: DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
      });
      const endDate = getFinalDate();

      const defaultFilter = getDefaultReturnFilter();

      switch (filter) {
        case ReturnFilterParam.returnsInProgress:
          setLocalStorageItem(LocalStorageKeys.hadUrlReturnFilterApplied, true);
          const {
            CREATED,
            AWAITING_CUSTOMER_DROPOFF,
            AWAITING_CUSTOMER_SHIPMENT,
            AWAITING_PRODUCT_ARRIVAL,
            PRODUCT_RECEIVED,
            RETURN_CONFIRMED,
            REFUNDED,
            REFUND_FAILED,
          } = RETURN_STATUSES;

          const waitCustomerFilter: ReturnFilterForm = {
            ...defaultFilter,
            startDate,
            endDate,
            returnStatuses: [
              CREATED.value,
              AWAITING_CUSTOMER_DROPOFF.value,
              AWAITING_CUSTOMER_SHIPMENT.value,
              AWAITING_PRODUCT_ARRIVAL.value,
              PRODUCT_RECEIVED.value,
              RETURN_CONFIRMED.value,
              REFUNDED.value,
              REFUND_FAILED.value,
            ].sort(),
          };

          setValues(waitCustomerFilter);
          setReturnFilter(waitCustomerFilter);
          break;

        default:
          if (
            getLocalStorageItem(LocalStorageKeys.hadUrlReturnFilterApplied) &&
            pathname === routes.exchangeAndReturn.path
          ) {
            clearUrlSearchParamFilter();
            setValues(defaultFilter);
            setReturnFilter(defaultFilter);
          }
          break;
      }
    }

    filterByUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (values.filterDate !== returnFilter.filterDate) {
      setValues(returnFilter);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnFilter.filterDate]);

  const { returnStatuses: selectedReturnStatuses } = returnFilter;

  const { day: startDateDay, month: startDateMonth } = formatTimestamp(
    values.startDate
  );
  const { day: endDateDay, month: endDateMonth } = formatTimestamp(
    values.endDate
  );

  const formattedStartDate = `${startDateDay}/${startDateMonth}`;
  const formattedEndDate = `${endDateDay}/${endDateMonth}`;

  const selectedReturnStatusesOptions = useMemo(
    () =>
      returnStatuses
        .map(({ label, value }) =>
          String(values.returnStatuses).split(',').includes(value)
            ? { label, value }
            : { label: '', value: '' }
        )
        .filter((status) => status.value !== ''),
    [values.returnStatuses]
  );

  return (
    <S.Main data-testid={TEST_IDS.CONSIGNMENT_FILTER} ref={mainRef}>
      <S.DropdownButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
        <div className="wrapper">
          <MdOutlineFilterAlt size={18} />
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            {formattedStartDate} a {formattedEndDate}
          </Text>
          {Boolean(selectedReturnStatuses.length) && (
            <Text size={FontSize.font12}>
              Status: <strong>{selectedReturnStatuses.length}</strong>
            </Text>
          )}
        </div>
        <S.ArrowWrapper $isOpen={isOpen}>
          <MdKeyboardArrowDown size={24} />
        </S.ArrowWrapper>
      </S.DropdownButton>

      {isOpen && (
        <>
          <S.Overlay onClick={() => setIsOpen(false)}></S.Overlay>
          <S.Form
            data-testid={TEST_IDS.CONSIGNMENT_FILTER_FORM}
            onSubmit={handleSubmit}
            onReset={handleReset}
            $parentPosition={mainRef.current?.getBoundingClientRect().top}
          >
            <S.DatePickerWrapper>
              <DateRangePicker
                onChangeRange={changeDateRange}
                onSubmit={submitForm}
                onReset={resetForm}
                values={{
                  startDate: toDate(values.startDate),
                  endDate: toDate(values.endDate),
                }}
              />
            </S.DatePickerWrapper>

            <S.FormSection>
              <S.SelectWrapper>
                <Text size={FontSize.font12}>Status da devolução</Text>
                <Select.Wrapper
                  key="returnStatuses"
                  name="returnStatuses"
                  onChange={handleChange}
                  options={returnStatuses}
                  initialTagged={selectedReturnStatusesOptions}
                  multiple
                  searchable
                >
                  <Select.Input placeholder="Selecionar" />

                  <Select.Tags maxTags={1} />

                  <Select.StatusArrow />

                  <Select.ClearButton />
                </Select.Wrapper>
              </S.SelectWrapper>
            </S.FormSection>

            <S.ButtonsWrapper>
              <Button variant="link" type="reset">
                <MdOutlineSettingsBackupRestore size={20} />
                Retornar ao Padrão
              </Button>
              <Button type="submit">
                <MdCheck size={20} />
                Aplicar
              </Button>
            </S.ButtonsWrapper>
          </S.Form>
        </>
      )}
    </S.Main>
  );
}
